import { Input } from "@/components/ui/input";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLanguageAndCurrency } from "@/hooks/useLanguageAndCurrency";
import { useToggle } from "@/hooks/useToggle";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getCardNumberMaxLength } from "@/utils/payment/cardInputFormatters";
import {
  createCardInputHandlers,
  prepareCardSubmissionData,
} from "@/utils/payment/cardFormHelpers";
import { createCardSchema } from "@/utils/payment/cardValidation";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import Image from "next/image";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Controller, useForm } from "react-hook-form";

const CreditCardForm = forwardRef(
  (
    {
      initialValues = {},
      onSubmit,
      onUnmaskedLengthMatch,
      value,
      onChange,
      cardProgramImage = "",
      showCardHolder = true,
      scrollToForm = true,
      isFloating = false,
    },
    ref,
  ) => {
    const t = useTranslationDetector();
    const isMobile = useIsMobile();
    const [isMobileTooltipOpen, toggleMobileTooltip] = useToggle(false);
    const cvvTooltipRef = useRef(null);
    const previousUnmaskedRef = useRef(null);
    const formRef = useRef(null);
    const fieldNamesRef = useRef(null);
    if (!fieldNamesRef.current) {
      fieldNamesRef.current = {
        cardHolder: `customer-entry-${Math.random().toString(36).slice(2, 10)}`,
        cardNumber: `customer-entry-${Math.random().toString(36).slice(2, 10)}`,
        expiry: `customer-entry-${Math.random().toString(36).slice(2, 10)}`,
        cvv: `customer-entry-${Math.random().toString(36).slice(2, 10)}`,
      };
    }
    const fieldNames = fieldNamesRef.current;
    const { selectedLanguage } = useLanguageAndCurrency();
    // Dinamik şema için state
    const [currentCardNumber, setCurrentCardNumber] = React.useState(
      initialValues.cardNumber || value?.cardNumber || "",
    );

    const schema = createCardSchema(t, currentCardNumber, showCardHolder);

    const {
      control,
      handleSubmit,
      formState: { errors },
      setValue,
      watch,
      getValues,
      clearErrors,
    } = useForm({
      resolver: zodResolver(schema),
      defaultValues: {
        cardNumber: initialValues.cardNumber || "",
        expiry: initialValues.expiry || "",
        cvv: initialValues.cvv || "",
        ...(showCardHolder && { name: initialValues.name || "" }),
      },

      mode: "onSubmit",
    });

    const { handleCardNumberChange, handleExpiryChange, handleCvvChange } =
      createCardInputHandlers({ onChange, errors, clearErrors });

    // Mount olduğunda scroll et
    useEffect(() => {
      if (formRef.current && scrollToForm) {
        formRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, []);

    useEffect(() => {
      if (!isMobile && isMobileTooltipOpen) {
        toggleMobileTooltip();
      }
    }, [isMobile, isMobileTooltipOpen, toggleMobileTooltip]);

    useEffect(() => {
      if (!isMobile || !isMobileTooltipOpen) {
        return;
      }

      const handleClickOutside = (event) => {
        if (
          cvvTooltipRef.current &&
          !cvvTooltipRef.current.contains(event.target)
        ) {
          toggleMobileTooltip();
        }
      };

      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, [isMobile, isMobileTooltipOpen, toggleMobileTooltip]);

    const watchedCardNumber = watch("cardNumber");

    // Kart numarası değiştiğinde şemayı güncelle
    useEffect(() => {
      if (watchedCardNumber !== currentCardNumber) {
        setCurrentCardNumber(watchedCardNumber);
      }
    }, [watchedCardNumber, currentCardNumber]);

    // Maskesiz uzunluk kontrolü - DÜZELTİLMİŞ VERSİYON
    useEffect(() => {
      const unmasked = watchedCardNumber.replace(/\D/g, "");
      const currentLength = unmasked.length;

      // Daha önce bu değeri işledik mi kontrol et
      if (previousUnmaskedRef.current === unmasked) {
        return; // Aynı değerse hiçbir şey yapma
      }

      let shouldTrigger = false;
      let valueToSend = null;

      if (currentLength === 8) {
        shouldTrigger = true;
        valueToSend = unmasked;
      } else if (currentLength === 15 || currentLength === 16) {
        shouldTrigger = true;
        valueToSend = unmasked.slice(0, 8);
      } else if (currentLength < 8 && previousUnmaskedRef.current !== null) {
        // 8'in altına düştüğünde bir kere tetikle (taksit tablosunu boşaltmak için)
        shouldTrigger = true;
        valueToSend = ""; // veya null - parent component'te kontrol edilecek
      }

      if (shouldTrigger && onUnmaskedLengthMatch) {
        previousUnmaskedRef.current = unmasked;
        onUnmaskedLengthMatch(valueToSend);
      } else if (
        currentLength < 8 ||
        (currentLength > 8 && currentLength < 15)
      ) {
        previousUnmaskedRef.current = null;
      }
    }, [watchedCardNumber, onUnmaskedLengthMatch]);

    // Dışarıdan value prop'u ile kontrol - DÜZELTİLMİŞ VERSİYON
    useEffect(() => {
      if (value?.cardNumber !== undefined) {
        setValue("cardNumber", value.cardNumber);

        const unmasked = value.cardNumber.replace(/\D/g, "");
        const length = unmasked.length;

        // Daha önce bu değeri işledik mi kontrol et
        if (previousUnmaskedRef.current === unmasked) {
          return;
        }

        if (length === 8) {
          previousUnmaskedRef.current = unmasked;
          if (onUnmaskedLengthMatch) {
            onUnmaskedLengthMatch(unmasked);
          }
        } else if (length === 15 || length === 16) {
          const firstEight = unmasked.slice(0, 8);
          previousUnmaskedRef.current = unmasked;
          if (onUnmaskedLengthMatch) {
            onUnmaskedLengthMatch(firstEight);
          }
        } else if (length < 8 && previousUnmaskedRef.current !== null) {
          // 8'in altına düştüğünde bir kere tetikle
          previousUnmaskedRef.current = unmasked;
          if (onUnmaskedLengthMatch) {
            onUnmaskedLengthMatch("");
          }
        }
      }
      if (value?.expiry !== undefined) setValue("expiry", value.expiry);
      if (value?.cvv !== undefined) setValue("cvv", value.cvv);
      if (value?.name !== undefined) setValue("name", value.name);
    }, [value, setValue, onUnmaskedLengthMatch]);

    // Form submit metodunu dışarıya aç
    useImperativeHandle(ref, () => ({
      submit: () => {
        return handleSubmit(
          (data) => {
            if (onSubmit) {
              onSubmit(prepareCardSubmissionData(data));
            }
          },
          (errors) => {},
        )();
      },
      getValue: (fieldName) => {
        return getValues(fieldName);
      },
      getValues: () => {
        return getValues();
      },
    }));

    const handleFormSubmit = (data) => {
      if (onSubmit) {
        onSubmit(prepareCardSubmissionData(data));
      }
    };

    return (
      <form
        ref={formRef}
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-4"
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
      >
        {showCardHolder && (
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                isFloating={isFloating}
                {...field}
                ref={field.ref}
                name={fieldNames.cardHolder}
                type="text"
                error={fieldState.error?.message}
                label={t("payment.cardHolder")}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
                aria-autocomplete="none"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (onChange) {
                    onChange({ name: e.target.value });
                  }
                }}
              />
            )}
          />
        )}
        <Controller
          name="cardNumber"
          control={control}
          render={({ field, fieldState }) => (
            <div className="relative">
              <Input
                isFloating={isFloating}
                {...field}
                ref={field.ref}
                name={fieldNames.cardNumber}
                type="tel"
                inputMode="numeric"
                maxLength={getCardNumberMaxLength(
                  (field.value || "").replace(/\D/g, ""),
                )}
                error={fieldState.error?.message}
                label={t("payment.cardNumber")}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-autocomplete="none"
                onChange={(e) => handleCardNumberChange(e, field)}
              />
              {cardProgramImage && cardProgramImage !== "Other" && (
                <Image
                  src={`${cdnCommonPath}${cardProgramImage}.svg`}
                  alt="Card Program"
                  className="absolute top-4 right-4"
                  width={38}
                  height={26}
                />
              )}
            </div>
          )}
        />
        <div className="flex gap-4">
          <Controller
            name="expiry"
            control={control}
            render={({ field, fieldState }) => (
              <div className="w-1/2">
                <Input
                  isFloating={isFloating}
                  {...field}
                  ref={field.ref}
                  name={fieldNames.expiry}
                  type="tel"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="__/__"
                  error={fieldState.error?.message}
                  label={t("payment.expiry")}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-autocomplete="none"
                  onChange={(e) => handleExpiryChange(e, field)}
                />
              </div>
            )}
          />

          <Controller
            name="cvv"
            control={control}
            render={({ field, fieldState }) => (
              <div className="relative w-1/2">
                <Input
                  isFloating={isFloating}
                  {...field}
                  ref={field.ref}
                  name={fieldNames.cvv}
                  type="tel"
                  inputMode="numeric"
                  maxLength={4}
                  inputClassName={selectedLanguage === "ar" ? "!pl-11" : ""}
                  error={fieldState.error?.message}
                  label={t("payment.cvv")}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-autocomplete="none"
                  onChange={(e) => handleCvvChange(e, field)}
                />
                <div className="absolute top-4 ltr:right-4 rtl:left-4">
                  <div
                    ref={cvvTooltipRef}
                    className="group relative flex items-center justify-center"
                  >
                    <button
                      type="button"
                      onClick={(event) => {
                        if (isMobile) {
                          event.preventDefault();
                          toggleMobileTooltip();
                        }
                      }}
                      className="text-foreground-subtle flex h-6 w-6 items-center justify-center rounded-full"
                      aria-label={t("common.cvvTooltip")}
                    >
                      <FontAwesomeIcon
                        className="cursor-pointer !text-xl"
                        icon={byPrefixAndName.fas["circle-info"]}
                      />
                    </button>
                    <div
                      className={clsx(
                        "shadow-small bg-foreground-subtle absolute right-0 bottom-full z-[1] w-48 rounded-lg p-3 text-xs text-white transition-all duration-150",
                        isMobile
                          ? isMobileTooltipOpen
                            ? "mb-2 block"
                            : "hidden"
                          : "hidden translate-y-1 opacity-0 md:pointer-events-none md:mb-2 md:block md:group-hover:translate-y-0 md:group-hover:opacity-100",
                      )}
                    >
                      {t("common.cvvTooltip")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </form>
    );
  },
);

CreditCardForm.displayName = "CreditCardForm";

export default CreditCardForm;
